import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, Download, X } from "lucide-react";

export default function PetId() {
  const [pets, setPets] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => axios.get(`${API}/pets`).then(r => setPets(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="pet-id-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold">Digital Pet ID</h1>
        <p className="text-ink-soft">Microchip & licence registry. QR collar tag. Travel-ready vaccination certificate.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {pets.map(p => {
          const scanUrl = `${window.location.origin}/found/${p.pet_id}`;
          return (
          <article key={p.pet_id} data-testid={`petid-card-${p.pet_id}`} className="bg-surface border border-line rounded-3xl overflow-hidden shadow-soft">
            <div className="p-5 flex gap-4">
              <img src={p.photo} className="w-20 h-20 rounded-2xl object-cover" alt=""/>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading text-xl font-extrabold">{p.name}</h3>
                  {p.registration && <span className="text-[10px] font-bold uppercase tracking-wider bg-leaf text-ink rounded-full px-2 py-0.5 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3"/>Registered</span>}
                </div>
                <div className="text-sm text-ink-soft">{p.breed} · {p.age_years}y · {p.species}</div>
                {p.registration ? (
                  <div className="mt-2 text-xs text-ink-soft">
                    <div>Microchip: <span className="font-bold text-ink">{p.registration.microchip_id}</span></div>
                    {p.registration.license_number && <div>Licence: <span className="font-bold text-ink">{p.registration.license_number}</span></div>}
                  </div>
                ) : (
                  <div className="mt-2 text-xs text-ink-soft">Not registered yet.</div>
                )}
              </div>
              <div className="hidden sm:block bg-surface-muted rounded-2xl p-2 border border-line" data-testid={`qr-thumb-${p.pet_id}`}>
                <QRCodeSVG value={scanUrl} size={68} bgColor="#F7F5EE" fgColor="#2C2C2C" level="M" />
              </div>
            </div>
            <div className="sm:hidden px-5 pb-3">
              <div className="flex items-center gap-3 bg-surface-muted rounded-2xl p-3 border border-line">
                <QRCodeSVG value={scanUrl} size={72} bgColor="#F7F5EE" fgColor="#2C2C2C" level="M" />
                <div className="text-xs text-ink-soft">Scan this tag if {p.name} is ever lost. Print and clip to collar.</div>
              </div>
            </div>
            <div className="px-5 pb-5 flex gap-2">
              <button data-testid={`qr-${p.pet_id}`} onClick={() => setOpen({ pet: p, mode: "qr" })} className="flex-1 bg-ink text-white rounded-full py-2.5 text-sm font-bold hover:bg-brand">
                {p.registration ? "View QR tag" : "Quick QR (no registration)"}
              </button>
              {!p.registration ? (
                <button data-testid={`register-${p.pet_id}`} onClick={() => setOpen({ pet: p, mode: "register" })} className="flex-1 bg-brand text-white rounded-full py-2.5 text-sm font-bold hover:bg-brand-hover">Register</button>
              ) : (
                <button data-testid={`cert-${p.pet_id}`} onClick={() => setOpen({ pet: p, mode: "cert" })} className="flex-1 bg-surface-muted text-ink rounded-full py-2.5 text-sm font-bold border border-line hover:border-brand">Certificate</button>
              )}
            </div>
          </article>
          );
        })}
      </div>

      {open?.mode === "register" && <RegisterModal pet={open.pet} onClose={() => { setOpen(null); load(); }}/>}
      {open?.mode === "qr" && <QrModal pet={open.pet} onClose={() => setOpen(null)}/>}
      {open?.mode === "cert" && <CertModal pet={open.pet} onClose={() => setOpen(null)}/>}
    </div>
  );
}

function RegisterModal({ pet, onClose }) {
  const [form, setForm] = useState({ microchip_id: "", license_number: "", owner_phone: "", owner_address: "", country: "IN" });
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/pets/${pet.pet_id}/register`, form);
    setDone(true);
  };
  return (
    <Modal onClose={onClose} title={`Register ${pet.name}`}>
      {!done ? (
        <form onSubmit={submit} className="space-y-3" data-testid="register-form">
          <F label="Microchip ID"><input required value={form.microchip_id} onChange={e=>setForm({...form, microchip_id:e.target.value})} className="li" placeholder="985 1219 4xxxxxx" data-testid="pet-register-microchip"/></F>
          <F label="Licence number (if any)"><input value={form.license_number} onChange={e=>setForm({...form, license_number:e.target.value})} className="li" placeholder="State / municipal licence" data-testid="pet-register-license"/></F>
          <F label="Owner phone (printed on QR tag)"><input required value={form.owner_phone} onChange={e=>setForm({...form, owner_phone:e.target.value})} className="li" data-testid="pet-register-phone"/></F>
          <F label="Owner address"><input value={form.owner_address} onChange={e=>setForm({...form, owner_address:e.target.value})} className="li" data-testid="pet-register-address"/></F>
          <F label="Country">
            <select value={form.country} onChange={e=>setForm({...form, country:e.target.value})} className="li">
              {["IN","US","GB","CA","AU","SG","AE"].map(c => <option key={c}>{c}</option>)}
            </select>
          </F>
          <button data-testid="register-submit" className="w-full bg-brand text-white rounded-full py-3 font-bold hover:bg-brand-hover">Register pet</button>
        </form>
      ) : (
        <div className="text-center py-6"><ShieldCheck className="w-12 h-12 text-leaf mx-auto mb-2"/><div className="font-heading text-xl font-extrabold">{pet.name} is registered!</div><p className="text-sm text-ink-soft mt-1">Your QR collar tag is ready.</p><button onClick={onClose} className="mt-4 bg-ink text-white rounded-full px-5 py-2 text-sm font-bold">View tag</button></div>
      )}
    </Modal>
  );
}

function QrModal({ pet, onClose }) {
  const scanUrl = `${window.location.origin}/found/${pet.pet_id}`;
  return (
    <Modal onClose={onClose} title={`${pet.name}'s QR Tag`}>
      <div className="text-center" data-testid="qr-modal">
        <div className="bg-surface-muted rounded-3xl p-6 inline-block">
          <QRCodeSVG value={scanUrl} size={220} bgColor="#F7F5EE" fgColor="#2C2C2C" level="H" includeMargin/>
        </div>
        <div className="mt-3 text-xs text-ink-soft">If found, scanning leads to:</div>
        <div className="text-sm font-bold break-all mt-1">{scanUrl}</div>
        <p className="text-xs text-ink-soft mt-3">Print this and clip to {pet.name}'s collar. If someone finds your pet, they can scan it to reach you.</p>
      </div>
    </Modal>
  );
}

function CertModal({ pet, onClose }) {
  const [cert, setCert] = useState(null);
  useEffect(() => { axios.get(`${API}/pets/${pet.pet_id}/certificate`).then(r => setCert(r.data)); }, [pet.pet_id]);
  return (
    <Modal onClose={onClose} title="Vaccination Certificate" wide>
      {cert ? (
        <div className="bg-bg border-2 border-ink rounded-2xl p-6" data-testid="cert-body">
          <div className="text-center border-b border-line pb-3">
            <div className="text-xs uppercase tracking-[0.2em] text-ink-soft font-bold">PetBond · Digital Vaccination Record</div>
            <h3 className="font-heading text-2xl font-extrabold mt-1">{cert.pet.name}</h3>
            <div className="text-sm text-ink-soft">{cert.pet.species} · {cert.pet.breed} · {cert.pet.age_years}y</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2 mt-4 text-sm">
            <div><strong>Owner:</strong> {cert.owner.name}</div>
            <div><strong>Issued:</strong> {new Date(cert.issued_at).toLocaleDateString()}</div>
            {cert.pet.registration?.microchip_id && <div><strong>Microchip:</strong> {cert.pet.registration.microchip_id}</div>}
            {cert.pet.registration?.license_number && <div><strong>Licence:</strong> {cert.pet.registration.license_number}</div>}
          </div>
          <div className="mt-4">
            <div className="text-xs font-bold uppercase tracking-wider text-ink-soft mb-2">Vaccinations</div>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-line"><th className="text-left py-1 font-bold">Vaccine</th><th className="text-left font-bold">Date</th><th className="text-left font-bold">Next due</th></tr></thead>
              <tbody>
                {(cert.pet.vaccinations || []).map((v,i) => <tr key={i} className="border-b border-line"><td className="py-1.5">{v.name}</td><td>{v.date}</td><td>{v.next_due}</td></tr>)}
                {(cert.pet.vaccinations || []).length === 0 && <tr><td className="py-2 text-ink-soft" colSpan="3">No vaccinations recorded.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="text-center mt-4 text-xs text-ink-soft">Verifiable digital record. Accepted by participating clinics, hostels, and travel partners.</div>
          <button onClick={() => window.print()} className="mt-4 w-full bg-ink text-white rounded-full py-2.5 text-sm font-bold inline-flex items-center justify-center gap-2"><Download className="w-4 h-4"/> Print / Save PDF</button>
        </div>
      ) : <div className="text-ink-soft">Loading…</div>}
    </Modal>
  );
}

function Modal({ children, onClose, title, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className={`bg-surface rounded-3xl p-6 w-full ${wide ? "max-w-xl" : "max-w-md"} border border-line shadow-soft max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl font-extrabold">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-surface-muted"><X className="w-4 h-4"/></button>
        </div>
        {children}
        <style>{`.li{width:100%;border:1px solid #EAE0D5;border-radius:1.5rem;padding:0.65rem 1rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.li:focus{border-color:#FF6B35;}`}</style>
      </div>
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
